/**
 * Voucher Redemption Locator — public search (edge function `voucher-locator`) and the admin settings
 * surface (voucher_locator_settings + admin RPCs, admins only by RLS/RPC checks).
 */
import { anonSupabase, supabase } from "./supabase";
import type { LocatorRadius, LocatorResponse } from "../utils/voucherLocator";

export interface LocatorSearch {
  code: string | null;
  zip?: string | null;
  lat?: number | null;
  lng?: number | null;
  radius: LocatorRadius;
  /** Arrival search: center on the voucher holder's ZIP (server-side; needs a code, no point). */
  auto?: boolean;
}

/**
 * Calls the public endpoint with the ANON key only (no member session is involved or wanted: the
 * page is opened from an email). Non-2xx bodies (400 invalid / 429 throttled) are still returned as a
 * LocatorResponse so the page can show the right message.
 */
export async function searchVoucherLocations(
  params: LocatorSearch,
): Promise<LocatorResponse> {
  const { data, error } = await anonSupabase.functions.invoke(
    "voucher-locator",
    {
      body: {
        code: params.code ?? undefined,
        zip: params.zip ?? undefined,
        lat: params.lat ?? undefined,
        lng: params.lng ?? undefined,
        radius: params.radius,
        auto: params.auto ? true : undefined,
      },
    },
  );
  if (error) {
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const body = (await ctx.json()) as LocatorResponse;
        if (body?.status) {
          return body;
        }
        if (ctx.status === 429) {
          return { status: "rate_limited" };
        }
      } catch {
        /* fall through */
      }
    }
    return { status: "error" };
  }
  return data as LocatorResponse;
}

// ---- admin -----------------------------------------------------------------------------------

export interface VoucherLocatorSettings {
  included_org_types: string[];
  require_active_voucher_cta: boolean;
  conceal_street_address_types: string[];
  concealed_pin_decimals: number;
  max_results: number;
  updated_at: string;
  updated_by: string | null;
}

export interface VoucherLocatorSummary {
  listed_total: number;
  by_type: {
    type: string;
    included: boolean;
    listed: number;
    no_pin: number;
    organizations: number;
  }[];
}

export interface VoucherLocatorListRow {
  organization_id: string;
  name: string;
  organization_type: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  website: string | null;
  street_address_hidden: boolean;
}

export async function getVoucherLocatorSettings(): Promise<VoucherLocatorSettings> {
  const { data, error } = await supabase
    .from("voucher_locator_settings")
    .select(
      "included_org_types, require_active_voucher_cta, conceal_street_address_types, concealed_pin_decimals, max_results, updated_at, updated_by",
    )
    .eq("id", true)
    .single();
  if (error) {
    throw error;
  }
  return data as VoucherLocatorSettings;
}

export async function saveVoucherLocatorSettings(
  patch: Pick<
    VoucherLocatorSettings,
    | "included_org_types"
    | "require_active_voucher_cta"
    | "conceal_street_address_types"
  >,
): Promise<void> {
  const { data, error } = await supabase
    .from("voucher_locator_settings")
    .update(patch)
    .eq("id", true)
    .select("updated_at");
  if (error) {
    throw error;
  }
  // RLS returns zero rows instead of an error for a caller who may not update — say so.
  if (!data || data.length === 0) {
    throw new Error("You don't have permission to change these settings.");
  }
}

export async function getVoucherLocatorSummary(): Promise<VoucherLocatorSummary> {
  const { data, error } = await supabase.rpc("voucher_locator_admin_summary");
  if (error) {
    throw error;
  }
  return data as VoucherLocatorSummary;
}

/** Pages through the whole list: PostgREST caps a response at 1,000 rows and PROD lists ~2,400. */
export async function getVoucherLocatorList(): Promise<
  VoucherLocatorListRow[]
> {
  const PAGE = 1000;
  const rows: VoucherLocatorListRow[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .rpc("voucher_locator_admin_list")
      .range(from, from + PAGE - 1);
    if (error) {
      throw error;
    }
    rows.push(...((data ?? []) as VoucherLocatorListRow[]));
    if (!data || data.length < PAGE) {
      return rows;
    }
  }
}
